import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { Store } from '@ngrx/store'
import { Observable, take, debounceTime, map, filter, switchMap, withLatestFrom } from 'rxjs'
import {
  CompanyAPIModel,
  CompanyEntityAPIModel,
  EmployeeAdminAssignmentPayload
} from '../../../../../../models/api.model'
import { getCompany, getEntities } from '../../../../../../store/selector/onboarding.selector'
import { OnboardingService } from '../../../../../../services/onboarding-wizard/onboarding.service'
import {
  assign_employee_admins,
  assign_employee_admins_success, onboardingGoToNextStep
} from '../../../../../../store/action/onboarding.action'
import { Actions, ofType } from '@ngrx/effects'

@Component({
  selector: 'app-admin-assignment',
  templateUrl: './admin-assignment.component.html',
  styleUrls: ['./admin-assignment.component.scss']
})
export class AdminAssignmentComponent implements OnInit {

  @Output() saveAssignmentAction = new EventEmitter<any>()
  @Output() goImport = new EventEmitter<void>()
  @Output() saveAndNext = new EventEmitter<any>()
  @Output() discard = new EventEmitter<void>()
  @Input() history: any[] = []


  assignmentForm!: FormGroup

  company$!: Observable<CompanyAPIModel>
  entities$!: Observable<CompanyEntityAPIModel[]>
  employeeResults: any[] = []
  private onboardingService: OnboardingService


  constructor(
    private fb: FormBuilder,
    private store: Store,
    onboardingService: OnboardingService,
    private readonly actions$: Actions
  ) {
    this.onboardingService = onboardingService
  }

  ngOnInit(): void {
    this.company$ = this.store.select(getCompany)
    this.entities$ = this.store.select(getEntities)

    this.assignmentForm = this.fb.group({
      employeeSearch: [''],
      selectedEmployees: [[]]
    })

    this.setupEmployeeSearch()

    this.actions$
      .pipe(ofType(assign_employee_admins_success))
      .subscribe(() => {
        this.assignmentForm.reset({
          employeeSearch: '',
          selectedEmployees: []
        })
        this.employeeResults = []
      })
  }

  onSaveAndNext(): void {
    const selected = this.assignmentForm.value.selectedEmployees || []

    if (!selected?.length && this.history?.length) {
      this.store.dispatch(
        onboardingGoToNextStep({
          props: { type: 'EMPLOYEE_ADMINS' }
        })
      )
      return
    }

    if (!selected?.length) return

    this.company$.pipe(take(1)).subscribe(company => {
      const payload: EmployeeAdminAssignmentPayload = {
        employeeIds: selected.map((x: any) => x.id),
        companyReferenceType: 'GROUP',
        companyReferenceId: company.id
      }

      this.store.dispatch(
        assign_employee_admins({
          payload,
          moveNext: true
        })
      )

      this.emitAssignmentHistory()
    })
  }

  private setupEmployeeSearch(): void {
    this.assignmentForm.get('employeeSearch')!.valueChanges
      .pipe(
        debounceTime(500),
        map(v => v?.trim()),
        filter(v => v && v.length >= 3),
        withLatestFrom(this.entities$),
        switchMap(([text, entities]) =>
          this.onboardingService.searchEmployees(
            text,
            entities.map(e => e.id)
          )
        )
      )
      .subscribe(results => {
        this.employeeResults = results
      })
  }


  addEmployee(emp: any): void {

    const current = this.assignmentForm.value.selectedEmployees || []

    if (current.some((e: any) => e.id === emp.id)) {
      return
    }

    const updated = [
      ...current,
      {
        id: emp.id,
        display: `${emp.fullName} (${emp.employeeCode})`
      }
    ]

    this.assignmentForm.patchValue({
      selectedEmployees: updated,
      employeeSearch: ''
    })

    this.employeeResults = []
  }

  saveAssignment(): void {
    const selected = this.assignmentForm.value.selectedEmployees
    if (!selected?.length) return

    this.company$.pipe(take(1)).subscribe(company => {
      const payload: EmployeeAdminAssignmentPayload = {
        employeeIds: selected.map((x: any) => x.id),
        companyReferenceType: 'GROUP',
        companyReferenceId: company.id
      }

      this.store.dispatch(assign_employee_admins({ payload }))
      this.emitAssignmentHistory()
    })
  }

  removeEmployee(emp: any): void {
    const updated = this.assignmentForm.value.selectedEmployees
      .filter((e: any) => e.id !== emp.id)

    this.assignmentForm.patchValue({
      selectedEmployees: updated
    })
  }

  private emitAssignmentHistory(): void {
    const selected = this.assignmentForm.value.selectedEmployees
    if (!selected?.length) return

    this.company$.pipe(take(1)).subscribe(company => {
      this.entities$.pipe(take(1)).subscribe(entities => {
        this.saveAssignmentAction.emit({
          employees: selected,
          company,
          entities
        })
      })
    })
  }
}
